/**
 * A leitura proposta de uma imagem de documento, confirmada no próprio cartão (E114.F3, R-05).
 *
 * Decisão do owner (27/09): a leitura do verso do RG é proposta automaticamente quando a imagem
 * entra no caso, e o perito a confere contra a imagem e confirma aqui, em um clique — sem ir a
 * "Documentos de apoio" na pré-análise, que continua existindo para corrigir um campo. Confirmar
 * grava pelo mesmo pedido de lá: a proposta vira a leitura confirmada, a única que o exame lê.
 *
 * E118.F1 (GUS-127, decisão do owner 28/09): a leitura com nome e CPF iguais ao cadastro entra no
 * documento sem clique (corroborada) e o botão continua; a que não confere fica pendente, com a
 * faixa de atenção — sem trava de emissão.
 */
import { useState } from 'react';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import {
  leituraPendente,
  type CandidataDeImagemData,
  type CandidatasDoCasoData,
  type LeituraDaImagemData,
} from '@/lib/candidatas_de_imagem';
import { confirmarLeitura, type LeituraDeIdentidadeData } from '@/lib/documentos_de_apoio';

const t = ptBR.analiseFotografica.candidatas.leitura;

type Campo = keyof typeof t.campos;
const CAMPOS: readonly Campo[] = [
  'nome',
  'cpf',
  'registro',
  'nascimento',
  'emitido_em',
  'filiacao',
  'naturalidade',
  'orgao_expedidor',
  'documento_de_origem',
];

function valorImpresso(proposta: LeituraDeIdentidadeData, campo: Campo): string | null {
  const valor = proposta[campo];
  if (valor === null || valor === undefined || valor === '') return null;
  if (campo === 'nascimento' || campo === 'emitido_em') {
    const [ano, mes, dia] = valor.split('-');
    return `${dia}/${mes}/${ano}`;
  }
  return valor;
}

/** A faixa da leitura pendente: o motivo pelo que foi lido (sem nome ou CPF, ou divergente). */
function motivoDaPendencia(proposta: LeituraDeIdentidadeData): string {
  return proposta.nome && proposta.cpf ? t.pendenteNaoConfere : t.pendenteSemDados;
}

export interface LeituraPropostaDaImagemProps {
  caseId: string;
  leitura: LeituraDaImagemData;
  bloqueado: boolean;
  onConfirmada: () => void;
}

export function LeituraPropostaDaImagem({
  caseId,
  leitura,
  bloqueado,
  onConfirmada,
}: LeituraPropostaDaImagemProps): JSX.Element {
  const client = useApiClient();
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const prefixo = `leitura-${leitura.documento_id}`;

  const confirmar = (): void => {
    void (async () => {
      setEnviando(true);
      setErro(null);
      try {
        await confirmarLeitura(client, caseId, leitura.documento_id, {
          leitura: leitura.proposta,
          origem: 'leitura_optica',
        });
        onConfirmada();
      } catch {
        setErro(t.erro);
      } finally {
        setEnviando(false);
      }
    })();
  };

  return (
    <div className="space-y-sc-2 border-t border-border pt-sc-2" data-testid={prefixo}>
      <p className="text-body-sm text-fg">{t.titulo}</p>
      <p className="text-caption text-fg-muted">{t.intro}</p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-sc-3 gap-y-1 text-body-sm">
        {CAMPOS.map((campo) => {
          const valor = valorImpresso(leitura.proposta, campo);
          if (valor === null) return null;
          return (
            <div key={campo} className="contents">
              <dt className="text-fg-muted">{t.campos[campo]}</dt>
              <dd className="text-fg" data-testid={`${prefixo}-${campo}`}>
                {valor}
              </dd>
            </div>
          );
        })}
      </dl>
      {leituraPendente(leitura) ? (
        <p
          role="status"
          className="border-l-2 border-warning bg-warning-50 px-sc-2 py-1 text-body-sm text-warning-700"
          data-testid={`${prefixo}-pendente`}
        >
          {motivoDaPendencia(leitura.proposta)}
        </p>
      ) : null}
      {leitura.corroborada === true && !leitura.confirmada ? (
        <p className="text-body-sm text-success-700" data-testid={`${prefixo}-corroborada`}>
          {t.corroborada}
        </p>
      ) : null}
      {leitura.confirmada ? (
        <p className="text-body-sm text-success-700" data-testid={`${prefixo}-confirmada`}>
          {t.confirmada}
        </p>
      ) : (
        // E207.F6.T03: a ação que pede o perito agora é a única sólida, como no cartão do apoio.
        <Button
          type="button"
          disabled={bloqueado || enviando}
          onClick={confirmar}
          data-testid={`${prefixo}-confirmar`}
        >
          {enviando ? t.confirmando : t.confirmar}
        </Button>
      )}
      {erro !== null ? (
        <p role="alert" className="text-fg-danger text-body-sm" data-testid={`${prefixo}-erro`}>
          {erro}
        </p>
      ) : null}
    </div>
  );
}

export interface LeiturasRetiradasProps {
  caseId: string;
  leituras: CandidatasDoCasoData | null;
  concluido: boolean;
  ocupado: boolean;
  onConfirmada: () => void;
}

/** As imagens de documento retiradas das propostas de retrato, cada uma com a sua leitura. */
export function LeiturasRetiradas({
  caseId,
  leituras,
  concluido,
  ocupado,
  onConfirmada,
}: LeiturasRetiradasProps): JSX.Element | null {
  const retiradas = (leituras?.leituras ?? []).filter((l) => l.retirada);
  if (retiradas.length === 0) return null;
  return (
    <section className="space-y-sc-3" data-testid="leituras-retiradas">
      <h4 className="text-body text-fg">{t.retiradasTitulo}</h4>
      <ul className="space-y-sc-3">
        {retiradas.map((leitura) => (
          <li key={leitura.documento_id} className="border border-border p-sc-3">
            <p className="truncate text-body-sm text-fg-muted">{leitura.documento_nome}</p>
            <LeituraPropostaDaImagem
              caseId={caseId}
              leitura={leitura}
              bloqueado={concluido || ocupado}
              onConfirmada={onConfirmada}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

/** E114.F3 (R-05): a leitura proposta da imagem desta candidata, se houver. */
export function leituraDaCandidata(
  dados: CandidatasDoCasoData,
  candidata: CandidataDeImagemData,
): LeituraDaImagemData | undefined {
  return dados.leituras?.find((l) => l.documento_id === candidata.documento_id && !l.retirada);
}
